// --- src/components/TestModeScreen.js ---
import React, { useState, useRef, useCallback, useEffect } from 'react';
import CompletionScreen from './CompletionScreen';
import './TestModeScreen.css';

function TestModeScreen({ quiz, onComplete, onBack, apiPort, llmSettings, onOpenHistoryModal, sessionProgress }) {
    const [mode, setMode] = useState('writing');
    const [userEssay, setUserEssay] = useState('');
    const [gradingResult, setGradingResult] = useState(null);
    const [selectedCommentId, setSelectedCommentId] = useState(null); 
    const [leftPanelView, setLeftPanelView] = useState('model'); 
    
    const [activeMobileTab, setActiveMobileTab] = useState('facts');
    const [leftPanelWidth, setLeftPanelWidth] = useState(450);
    const [isResizing, setIsResizing] = useState(false);

    const feedbackListRef = useRef(null);

    useEffect(() => {
        setMode('writing');
        setUserEssay('');
        setGradingResult(null);
        setSelectedCommentId(null);
        setLeftPanelView('model');
        setActiveMobileTab('facts');
    }, [quiz.id]);

    // Auto-scroll to feedback card when a highlight is clicked
    useEffect(() => {
        if (selectedCommentId && feedbackListRef.current) {
            const card = feedbackListRef.current.querySelector(`[data-comment-id="${selectedCommentId}"]`);
            if (card) {
                card.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }
    }, [selectedCommentId]);

    const startResizing = useCallback((mouseDownEvent) => {
        mouseDownEvent.preventDefault();
        setIsResizing(true);
        const onMouseMove = (mouseMoveEvent) => {
            const newWidth = mouseMoveEvent.clientX;
            const minWidth = 300;
            const maxWidth = window.innerWidth - 600; // Account for right panel
            if (newWidth > minWidth && newWidth < maxWidth) setLeftPanelWidth(newWidth);
        };
        const onMouseUp = () => {
            setIsResizing(false);
            window.removeEventListener("mousemove", onMouseMove);
            window.removeEventListener("mouseup", onMouseUp);
            document.body.style.cursor = "default";
            document.body.style.userSelect = "auto";
        };
        window.addEventListener("mousemove", onMouseMove);
        window.addEventListener("mouseup", onMouseUp);
        document.body.style.cursor = "col-resize";
        document.body.style.userSelect = "none";
    }, []);

    const handleHighlightSelection = () => {
        const selection = window.getSelection();
        if (!selection.rangeCount || selection.isCollapsed) return;
        const range = selection.getRangeAt(0);
        const container = document.getElementById('fact-pattern-content');
        if (container && container.contains(range.commonAncestorContainer)) {
            try {
                const span = document.createElement('span');
                span.className = 'highlighted';
                range.surroundContents(span);
                selection.removeAllRanges(); 
            } catch (e) { console.warn("Highlight failed."); }
        }
    };

    const handleFactClick = (e) => {
        if (e.target.classList.contains('highlighted')) {
            const textContent = document.createTextNode(e.target.textContent);
            e.target.parentNode.replaceChild(textContent, e.target);
        }
    };

    const handleGradeSubmission = async () => {
        if (!userEssay.trim()) { alert("Please write an answer first."); return; }
        setMode('grading');
        try {
            const payload = {
                factPattern: quiz.fact_pattern,
                modelAnswer: quiz.answer_key || { issue: "N/A", rule: "N/A", analysis: "N/A", conclusion: "N/A" },
                userAnswer: userEssay,
                llmSettings: llmSettings
            };
            const response = await fetch(`/api/grade-hypo`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (!response.ok) throw new Error("Grading failed");
            const data = await response.json();
            let normalizedScore = data.score_suggestion;
            if (normalizedScore > 5) {
                normalizedScore = Math.round(normalizedScore / 20);
                normalizedScore = Math.max(1, Math.min(5, normalizedScore));
                data.score_suggestion = normalizedScore;
            }
            setGradingResult(data);
            setMode('feedback');
        } catch (error) {
            onComplete(quiz.id, 3, userEssay, "Grading failed.");
            setMode('completed');
        }
    };

    const handleInteraction = (e) => {
        const marker = e.target.closest('.annotated-text-view span[id]');
        if (!marker) return;
        e.stopPropagation(); 
        const numericId = parseInt(marker.getAttribute('id').replace(/\D/g, ''), 10);
        
        // Toggle if already selected, otherwise select
        if (selectedCommentId === numericId) {
            setSelectedCommentId(null);
        } else {
            setSelectedCommentId(numericId);
        }
    };

    const handleLinkedRuleClick = (e) => {
        e.stopPropagation();
        if (quiz.related_rule_ids?.length > 0) {
            onOpenHistoryModal(quiz.related_rule_ids[quiz.related_rule_ids.length - 1]);
        }
    };

    if (mode === 'completed') return <CompletionScreen onComplete={() => onComplete(quiz.id, gradingResult?.score_suggestion || 3, userEssay, gradingResult)} />;
    if (mode === 'grading') return (
        <div className="test-mode-container theme-aware grading-loading">
            <div className="loading-spinner"></div>
            <h2>Wali's&trade; Grading...</h2>
        </div>
    );

    const isMobile = window.innerWidth < 900;

    // --- RENDER: FEEDBACK MODE ---
    if (mode === 'feedback' && gradingResult) {
        // Calculate Grid Layout: Left (Variable) | User (Flex) | Right (Fixed 350px)
        const gridStyle = isMobile 
            ? { gridTemplateColumns: '1fr' } 
            : { gridTemplateColumns: `${leftPanelWidth}px 10px 1fr 350px` };

        return (
            <div className="test-mode-container theme-aware">
                <header className="test-mode-header">
                    <div style={{display:'flex', alignItems:'center', gap:'15px'}}>
                        <button className="btn secondary" style={{border:'none'}} onClick={() => setMode('completed')}>&larr; Exit Review</button>
                        <div id="test-header-info">
                            <span className="grade-badge">Grade: {gradingResult.score_suggestion}/5</span>
                            <span className="quiz-title">{quiz.ruleName}</span>
                        </div>
                    </div>
                    <button className="btn" onClick={() => setMode('completed')}>Finish Review</button>
                </header>

                <div className="resizable-grid feedback-layout" style={gridStyle}>
                    
                    {/* LEFT PANEL: MODEL / FACTS */}
                    <div className={`exam-left-panel ${activeMobileTab === 'facts' ? 'active-panel' : ''}`}>
                        <div className="panel-toggle-header">
                            <button className={`toggle-btn ${leftPanelView === 'model' ? 'active' : ''}`} onClick={() => setLeftPanelView('model')}>Model Analysis</button>
                            <button className={`toggle-btn ${leftPanelView === 'facts' ? 'active' : ''}`} onClick={() => setLeftPanelView('facts')}>Fact Pattern</button>
                        </div>
                        <div className="facts-content">
                            {leftPanelView === 'model' ? (
                                <>
                                    <div className="model-section"><h4>Issue</h4><p>{gradingResult.model_answer?.issue || quiz.answer_key?.issue}</p></div>
                                    <div className="model-section">
                                        <h4>Rule
                                            {quiz.related_rule_ids?.length > 0 && (
                                                <button className="linked-rule-chip" onClick={handleLinkedRuleClick}>
                                                    <span className="material-symbols-outlined" style={{fontSize: '1.1em'}}>gavel</span> Related Rule
                                                </button>
                                            )}
                                        </h4>
                                        <p>{gradingResult.model_answer?.rule || quiz.answer_key?.rule}</p>
                                    </div>
                                    <div className="model-section"><h4>Analysis</h4><p>{gradingResult.model_answer?.analysis || quiz.answer_key?.analysis}</p></div>
                                    <div className="model-section"><h4>Conclusion</h4><p>{gradingResult.model_answer?.conclusion || quiz.answer_key?.conclusion}</p></div>
                                </>
                            ) : (
                                <div dangerouslySetInnerHTML={{__html: quiz.fact_pattern}} />
                            )}
                        </div>
                    </div>

                    {/* RESIZER */}
                    {!isMobile && <div className={`resizer-handle ${isResizing ? 'active' : ''}`} onMouseDown={startResizing}><div className="handle-grip"></div></div>}

                    {/* CENTER PANEL: USER ANSWER */}
                    <div className={`exam-center-panel ${activeMobileTab === 'editor' ? 'active-panel' : ''}`}>
                         <div className="panel-toggle-header">
                            <span className="panel-title" style={{width:'100%', textAlign:'center'}}>Your Answer</span>
                        </div>
                        {/* 
                            We inject a dynamically created style block or use a prop to highlight the selected ID.
                            Actually, simple class manipulation via CSS based on selectedCommentId is tricky in dangerouslySetInnerHTML without re-parsing.
                            Alternative: The CSS uses attribute selector [id="N"] combined with a parent class?
                            Better: We attach a class to the container equal to the selected ID, e.g. "selected-1". 
                        */}
                        <div 
                            className={`annotated-text-view ${selectedCommentId ? `focus-mode focus-${selectedCommentId}` : ''}`} 
                            style={{padding: '40px'}} 
                            dangerouslySetInnerHTML={{ __html: gradingResult.annotated_user_answer_html || userEssay }} 
                            onClick={handleInteraction} 
                        />
                    </div>

                    {/* RIGHT PANEL: FEEDBACK CARDS */}
                    <div className={`exam-right-panel feedback-sidebar ${activeMobileTab === 'feedback' ? 'active-panel' : ''}`} ref={feedbackListRef}>
                        <div className="panel-toggle-header">
                            <span className="panel-title right-aligned" style={{width:'100%'}}>Feedback</span>
                        </div>
                        <div className="feedback-list">
                            {gradingResult.comments?.map((comment) => (
                                <div 
                                    key={comment.id} 
                                    data-comment-id={comment.id}
                                    className={`feedback-card ${selectedCommentId === comment.id ? 'active' : ''}`}
                                    onClick={() => setSelectedCommentId(comment.id)}
                                >
                                    <div className="feedback-header">
                                        <span className="feedback-badge">{comment.id}</span>
                                        <span className="feedback-label">Note</span>
                                    </div>
                                    <p className="feedback-text">{comment.feedback}</p>
                                </div>
                            ))}
                             {(!gradingResult.comments || gradingResult.comments.length === 0) && (
                                <div style={{padding:'20px', color:'var(--text-muted)', fontStyle:'italic'}}>No specific feedback generated.</div>
                            )}
                        </div>
                    </div>

                    {isMobile && (
                        <div className="exam-mobile-tabs">
                            <button className={`exam-tab-btn ${activeMobileTab === 'facts' ? 'active' : ''}`} onClick={() => setActiveMobileTab('facts')}>Facts</button>
                            <button className={`exam-tab-btn ${activeMobileTab === 'editor' ? 'active' : ''}`} onClick={() => setActiveMobileTab('editor')}>Answer</button>
                            <button className={`exam-tab-btn ${activeMobileTab === 'feedback' ? 'active' : ''}`} onClick={() => setActiveMobileTab('feedback')}>Notes</button>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // --- RENDER: WRITING MODE (Original 2-pane) ---
    return (
        <div className="test-mode-container theme-aware">
            <header className="test-mode-header">
                <div style={{display:'flex', alignItems:'center', gap:'15px'}}>
                    <button className="btn secondary" style={{border:'none'}} onClick={onBack}>&larr; Exit Exam</button>
                    <div id="test-header-info"><span className="quiz-title">{quiz.ruleName}</span></div>
                </div>
                <button className="btn" onClick={handleGradeSubmission}>Submit for Grading</button>
            </header>

            <div className="resizable-grid" style={{ gridTemplateColumns: isMobile ? '1fr' : `${leftPanelWidth}px 10px 1fr` }}>
                <div className={`exam-left-panel ${activeMobileTab === 'facts' ? 'active-panel' : ''}`}>
                    <div className="sticky-issue">
                        <span className="issue-label">Issue to Address</span>
                        <div style={{fontWeight:700, color:'var(--accent-color-primary)'}}>{quiz.answer_key?.issue || "Analyze the provided facts."}</div>
                    </div>
                    <div className="highlight-toolbar">
                        <button className="highlight-btn" onClick={handleHighlightSelection}><span className="material-symbols-outlined">ink_highlighter</span> Highlight</button>
                    </div>
                    <div className="facts-content" id="fact-pattern-content" onClick={handleFactClick} dangerouslySetInnerHTML={{__html: quiz.fact_pattern}} />
                </div>

                {!isMobile && <div className={`resizer-handle ${isResizing ? 'active' : ''}`} onMouseDown={startResizing}><div className="handle-grip"></div></div>}

                <div className={`exam-right-panel ${activeMobileTab === 'editor' ? 'active-panel' : ''}`}>
                    <textarea className="editor-textarea" placeholder="Type your analysis here (IRAC format)..." value={userEssay} onChange={(e) => setUserEssay(e.target.value)} spellCheck="false" />
                </div>

                {isMobile && (
                    <div className="exam-mobile-tabs">
                        <button className={`exam-tab-btn ${activeMobileTab === 'facts' ? 'active' : ''}`} onClick={() => setActiveMobileTab('facts')}>Fact Pattern</button>
                        <button className={`exam-tab-btn ${activeMobileTab === 'editor' ? 'active' : ''}`} onClick={() => setActiveMobileTab('editor')}>My Answer</button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default TestModeScreen;